import { ReactNode, useEffect, useReducer } from 'react';
import { AIStarsIcon } from '../../../assets/svg';
import {
  confirmChatAction,
  rejectChatAction,
} from '../../../api/conversations.api';
import { Button } from '../../../components/button/button';
import { ChatActionProposal } from '../../../types/supportTicket.types';
import {
  actionTypeLabel,
  ACTION_VIEW_NOTE,
  ACTION_VIEW_TEXT,
  actionCardView,
  ActionCardView,
} from '../helpers/chatTriage';
import { useChatCardAction } from '../hooks/useChatCardAction';
import {
  CardButtons,
  CardChip,
  CardFooter,
  CardKicker,
  CardPrompt,
  CardStatusRow,
  CardSurface,
  CardTitle,
  CardTone,
} from './MessageCards.styled';

/** setTimeout runs immediately past this, so a far-off deadline never sleeps. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const VIEW_TONE: Record<Exclude<ActionCardView, 'live'>, CardTone> = {
  working: 'info',
  done: 'good',
  failed: 'bad',
  declined: 'neutral',
  expired: 'neutral',
  superseded: 'neutral',
};

type Props = {
  proposal: ChatActionProposal;
  /** Merchant side. The admin sees the same card with no way to act on it. */
  interactive: boolean;
  /** Time and ticks, when this card is the whole message. */
  meta?: ReactNode;
};

/**
 * What the bot is offering to do, and the merchant's yes or no.
 *
 * The card is the source of truth for what happened, not the button that was
 * pressed: confirming answers 200 whatever the outcome, and the state that
 * follows arrives by socket. See `useChatCardAction`.
 */
export const ActionProposalCard = ({ proposal, interactive, meta }: Props) => {
  const { busy, run, settle } = useChatCardAction();
  const [, tick] = useReducer((count: number) => count + 1, 0);

  /*
   * A live card ages into an expired one while it is being looked at, so it
   * carries its own alarm rather than waiting for something else to re-render
   * it. One timeout, at the deadline itself - a ticking interval would re-render
   * every open card in the thread once a second for a line that only changes
   * once.
   */
  useEffect(() => {
    if (proposal.status !== 'pending') return undefined;
    const delay = new Date(proposal.expiresAt).getTime() - Date.now();
    if (delay <= 0 || delay > MAX_TIMEOUT_MS) return undefined;
    const timer = setTimeout(tick, delay);
    return () => clearTimeout(timer);
  }, [proposal.status, proposal.expiresAt]);

  /* The wait ends when the entity moves, which is the only honest signal that
     the request landed - the response said nothing. */
  useEffect(() => {
    if (proposal.status !== 'pending') settle();
  }, [proposal.status, settle]);

  const view = actionCardView(proposal, Date.now());
  /* Read through a default: an API build older than the field sends a card
     with no `options` key at all, and that card is a plain Confirm. */
  const options = proposal.options ?? [];

  return (
    <CardSurface>
      <CardKicker>
        <AIStarsIcon />
        {actionTypeLabel(proposal.actionType)}
      </CardKicker>

      <CardTitle>{proposal.summary}</CardTitle>

      {view === 'live' && interactive && (
        /* Held rather than tapped, this is a message like any other and the
           bubble opens its action sheet under the finger. The sheet has nothing
           to offer for a bot message, and it swallows the tap that was meant
           for the button. */
        <CardButtons
          $wrap={options.length > 0}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {/* One button per way of saying yes, labelled by the backend, or the
              single Confirm when a card offers only the one. Every button locks
              while any of them is in flight - a second tap on the other option
              would be a second answer to a question already answered. */}
          {options.length > 0 ? (
            options.map((option) => (
              <Button
                key={option.key}
                $selected
                $small
                $loading={busy === option.key}
                data-loading={busy === option.key ? 'true' : undefined}
                disabled={!!busy}
                onClick={() =>
                  run(option.key, () =>
                    confirmChatAction(proposal.id, option.key)
                  )
                }
              >
                {option.label}
              </Button>
            ))
          ) : (
            <Button
              $selected
              $small
              $loading={busy === 'confirm'}
              data-loading={busy === 'confirm' ? 'true' : undefined}
              disabled={!!busy}
              onClick={() =>
                run('confirm', () => confirmChatAction(proposal.id))
              }
            >
              Confirm
            </Button>
          )}
          <Button
            $selected
            $gray
            $small
            $loading={busy === 'reject'}
            data-loading={busy === 'reject' ? 'true' : undefined}
            disabled={!!busy}
            onClick={() => run('reject', () => rejectChatAction(proposal.id))}
          >
            Cancel
          </Button>
        </CardButtons>
      )}

      {view === 'live' && !interactive && (
        <CardStatusRow>
          <CardChip $tone="wait">Waiting for the merchant</CardChip>
          {meta}
        </CardStatusRow>
      )}

      {view !== 'live' && (
        <>
          <CardStatusRow>
            <CardChip $tone={VIEW_TONE[view]}>
              {ACTION_VIEW_TEXT[view]}
            </CardChip>
            {!ACTION_VIEW_NOTE[view] && meta}
          </CardStatusRow>

          {ACTION_VIEW_NOTE[view] && (
            <CardStatusRow>
              <CardPrompt>{ACTION_VIEW_NOTE[view]}</CardPrompt>
              {meta}
            </CardStatusRow>
          )}
        </>
      )}

      {/* Only the live card still needs a line of its own: its buttons are the
          last thing in the card and the time belongs under them. */}
      {view === 'live' && interactive && meta && (
        <CardFooter>{meta}</CardFooter>
      )}
    </CardSurface>
  );
};
